import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Theme, darken, lighten } from "@mui/material";
import { RootState } from "@store/store";
import { CompanyData } from "@interfaces/store";
import { CustomSidebar } from "@interfaces/theme";
import { configuraciones, resolveRailPosition, resolveStageIndex } from "@components/ui_v2/onboardingRail";

/**
 * Nombre a mostrar de la empresa/solicitud activa. Para PFAE la razón social no existe,
 * así que se arma con el nombre y apellidos de la persona física.
 */
export const getCompanyDisplayName = (company: CompanyData) => {
  if (company.is_pfae) {
    return [company.pfae_name, company.pfae_last_name, company.pfae_last_name_2]
      .filter((part) => part?.trim())
      .join(" ")
      .trim();
  }
  return (company.legal_name || company.name || "").trim();
};

export const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("");

/** Ancho del sidebar de escritorio y del menú lateral en móvil. */
export const SIDEBAR_WIDTH = 260;

/** Alto del logo en el sidebar: intermedio entre los 22px de `.sb-logo img` del diseño y el tamaño del navbar legacy. */
export const SIDEBAR_LOGO_HEIGHT = 40;

/** Blanco translúcido de `.sb-dark` del diseño, por nivel de énfasis. */
export const white = (opacity: number) => `rgba(255,255,255,${opacity})`;

/**
 * Sidebar oscuro de `.sb-dark` del diseño (override de Flux), derivado de la paleta del tenant.
 * Es el que se usa cuando la marca no define `palette.components.sidebar`:
 * - `bg`: `primary.main` oscurecido (`#253F3A` → `#172D29` en Flux);
 * - `mark`: `primary.main` aclarado para los números y checks del rail (`#3A5C54`);
 * - `dot`: `primary.accent` aclarado para el punto de la fase activa (`#B5CEAF`);
 * - textos y superficies en blanco translúcido.
 */
const getDarkSidebarColors = (theme: Theme): CustomSidebar => {
  const { main, accent } = theme.palette.primary;
  const bg = darken(main, 0.38);
  const mark = lighten(main, 0.12);
  const dot = lighten(accent, 0.3);
  const markRing = `color-mix(in srgb, ${mark} 28%, transparent)`;
  return {
    bg,
    border: bg,
    divider: white(0.08),
    org: {
      bg: white(0.06),
      border: white(0.1),
      hoverBg: white(0.1),
      hoverBorder: white(0.18),
      shadow: "none",
      avatarBg: `linear-gradient(135deg, ${mark} 0%, ${main} 100%)`,
      avatarText: "#fff",
      name: "#fff",
      rfc: white(0.45),
      chevron: white(0.28),
    },
    rail: {
      label: white(0.32),
      labelActive: white(0.92),
      labelDone: white(0.48),
      numBg: white(0.08),
      numBorder: white(0.12),
      numText: white(0.38),
      numActiveBg: mark,
      numActiveText: "#fff",
      numActiveRing: markRing,
      numDoneBg: mark,
      numDoneText: "#fff",
      line: white(0.09),
      itemText: white(0.38),
      itemActiveText: "#fff",
      itemActiveBg: `color-mix(in srgb, ${mark} 20%, transparent)`,
      itemDoneText: white(0.42),
      markBg: bg,
      markBorder: white(0.18),
      markActiveBg: `color-mix(in srgb, ${mark} 24%, transparent)`,
      markActiveBorder: mark,
      markDoneBg: mark,
      dot: white(0.3),
      dotActive: dot,
    },
    foot: {
      avatarBg: white(0.1),
      avatarText: white(0.72),
      name: "#fff",
      mail: white(0.38),
      iconBtn: white(0.32),
      iconBtnHoverBg: white(0.08),
      iconBtnHoverText: "#fff",
      savedText: white(0.28),
      savedIcon: dot,
    },
    loginButton: {
      text: "#fff",
      border: white(0.28),
      hoverBorder: white(0.5),
      hoverBg: white(0.08),
    },
    mobile: {
      bg,
      border: "transparent",
      shadow: "0 2px 8px rgba(0,0,0,.18)",
      title: "#fff",
      subtitle: white(0.48),
      iconBtn: white(0.6),
      iconBtnHoverBg: white(0.08),
      progressTrack: white(0.12),
      progressBar: dot,
    },
  };
};

/** El fondo es oscuro si MUI elegiría texto blanco encima: decide qué logo va (claro u oscuro). */
const isDarkBg = (theme: Theme, bg: string) => theme.palette.getContrastText(bg) === theme.palette.common.white;

/**
 * Colores del sidebar y del navbar móvil: los de `palette.components.sidebar` si la marca los
 * define (ej. el sidebar claro de Mercader) y, si no, el oscuro derivado de `primary.main`.
 * `isDark`/`mobileIsDark` deciden si encima va `lateral.logo_bg_dark` o el logo del navbar.
 */
export const getSidebarColors = (theme: Theme) => {
  const colors = theme.palette.components?.sidebar ?? getDarkSidebarColors(theme);
  return {
    ...colors,
    isDark: isDarkBg(theme, colors.bg),
    mobileIsDark: isDarkBg(theme, colors.mobile.bg),
  };
};

/** Botón "Iniciar sesión" del sidebar y del navbar móvil (`loginButton` de la config). */
export const loginButtonSx = {
  textTransform: "none",
  fontFamily: (theme: Theme) => theme.typography.fontFamily,
  fontWeight: 600,
  color: (theme: Theme) => getSidebarColors(theme).loginButton.text,
  borderColor: (theme: Theme) => getSidebarColors(theme).loginButton.border,
  borderRadius: "8px",
  "&:hover": {
    borderColor: (theme: Theme) => getSidebarColors(theme).loginButton.hoverBorder,
    bgcolor: (theme: Theme) => getSidebarColors(theme).loginButton.hoverBg,
  },
} as const;

/**
 * Posición actual del rail (grupo y fase activos), compartida por el sidebar de escritorio y el
 * navbar móvil. Antes de que exista solicitud no hay status, así que cae al primer grupo/fase.
 */
export const useTofuRail = () => {
  const { pathname } = useLocation();
  const loanStatus = useSelector((state: RootState) => state.register.loan_status);
  const statusConfig = useSelector((state: RootState) => state.app.status_config);

  // Hoy solo está definido PM; PFAE recorre la misma secuencia de fases.
  const groups = configuraciones.PM;
  // Etapa: por `RAIL_STAGE_STARTS` / `RAIL_STAGE_ENDS` sobre el orden del `status_config`.
  const activeGroupIndex = resolveStageIndex(
    groups,
    statusConfig.map((item) => item.code),
    loanStatus,
    pathname,
  );
  // Fase dentro de la etapa (hoy oculta en el rail; ver `SHOW_RAIL_PHASES`).
  const { groupIndex, phaseIndex } = resolveRailPosition(groups, loanStatus, pathname);
  const activeIndex = groupIndex === activeGroupIndex && phaseIndex !== -1 ? phaseIndex : 0;
  const phases = groups[activeGroupIndex].phases;

  return {
    groups,
    activeGroupIndex,
    activeIndex,
    phases,
    activePhase: phases[activeIndex],
    activeGroup: groups[activeGroupIndex],
    /** Fases completadas + la actual, sobre las fases del grupo activo. */
    progress: phases.length ? ((activeIndex + 1) / phases.length) * 100 : 0,
    /** Etapas completadas + la actual, sobre las etapas (Onboarding, Expediente, Firma). */
    groupProgress: groups.length ? ((activeGroupIndex + 1) / groups.length) * 100 : 0,
  };
};
